<script setup lang="ts">
// import CookieBanner from "@/components/layouts/private/CookieBanner.vue";
// import Cookies from "js-cookie";
import { onMounted, ref } from "vue";
import { provideSectionNavigation } from '@/lib/useSectionNavigation.ts'
import PageProgressBar from '@/components/shared/progress-bar/PageProgressBar.vue'
import { usePageProgress } from '@/composables/usePageProgress.ts'
import { useSchoolStore } from '@/stores/school_store.ts'
import { Toaster } from '@/components/ui/toast'
import useFeatureAccess from '@/composables/useFeatureAccess.ts';
import LiquidGlassFilters from '@/components/effects/LiquidGlassFilters.vue';
import { useRoute, useRouter } from 'vue-router';
import GuideWalkthrough from '@/components/ai-chatbot/GuideWalkthrough.vue';

import { geographicalApi } from '@/services/geographical_api';
import { cambodiaAdminApi } from '@/services/cambodia_admin_api';
import appPackage from '../package.json';

const progressBarRef = ref();
const { setProgressBarInstance } = usePageProgress();
const schoolStore = useSchoolStore();
const featureAccess = useFeatureAccess();
const router = useRouter();
const route = useRoute();

// Clear geographical caches when app version changes
const clearGeoCacheOnVersionChange = () => {
  const GEO_APP_VERSION_KEY = 'geo_app_version';
  const currentVersion = appPackage.version;
  const storedVersion = localStorage.getItem(GEO_APP_VERSION_KEY);

  if (storedVersion !== currentVersion) {
    console.log(`App version changed (${storedVersion} -> ${currentVersion}), clearing geographical caches...`);
    geographicalApi.clearCache();
    cambodiaAdminApi.clearCache();
    localStorage.setItem(GEO_APP_VERSION_KEY, currentVersion);
  }
};

// const sessionExpire = Cookies.get('ssm-session-expire');
onMounted(async () => {
  // Clear stale geographical caches on app update
  clearGeoCacheOnVersionChange();

  // Initialize school store from localStorage
  console.log('Safari Debug: Initializing school store');
  schoolStore.getSelectedSchool();

  console.log('Safari Debug: School loaded from localStorage:', schoolStore.selectedSchool);

  // Skip feature fetch on auth pages (select-branch, select-school)
  // Features will be fetched AFTER branch selection in the selectBranch page
  const currentPath = router.currentRoute.value.path;
  const isAuthPage = currentPath.includes('/select-branch') || currentPath.includes('/select-school');

  // Fetch features if school is available (important for page refresh/app initialization)
  // BUT skip if we're on an auth page where branch context isn't established yet
  if (schoolStore.selectedSchool?.id && !isAuthPage) {
    try {
      await featureAccess.fetchFeatures();
      console.log('Safari Debug: Features fetched successfully on app mount');
    } catch (featureError) {
      console.log('Safari Debug: Could not fetch features on app mount:', featureError);
    }
  } else if (isAuthPage) {
    console.log('Safari Debug: Skipping feature fetch on auth page (branch not selected yet)');
  } else {
    console.log('Safari Debug: No school selected on app mount, skipping feature fetch');
  }

  // Set the progress bar instance for global access
  if (progressBarRef.value) {
    setProgressBarInstance(progressBarRef.value);
  }

//   if (new Date().getTime() >= parseInt(sessionExpire || '0')) {
//     Cookies.remove('ssm-session');
//     Cookies.remove('ssm-session-expire');
//   }
});

provideSectionNavigation()
</script>

<template>
  <!-- Page Progress Bar -->
  <PageProgressBar ref="progressBarRef" />

  <!-- Liquid Glass SVG Filters (Progressive Enhancement for Chrome) -->
  <LiquidGlassFilters />

  <!-- No keep-alive: keyed by fullPath it cached a full layout and page for every
       URL ever visited, so memory and per-navigation work grew without bound. -->
  <router-view v-slot="{ Component }">
    <component :is="Component" :key="$route.fullPath"></component>
  </router-view>

  <!-- Step-by-step guide: mounted once, on the signed-in /app pages only. -->
  <GuideWalkthrough v-if="route.path.startsWith('/app')" />

  <!-- Toast Provider -->
  <Toaster />
<!--  <CookieBanner/>-->
</template>

<style scoped>
</style>

<style>
/* Global Pure Frosted Glass Effect */

/* Override any bg-popover or bg-background classes that might conflict */
.frosted-glass-dropdown.bg-popover,
.frosted-glass-dialog.bg-popover,
[data-radix-dropdown-menu-content].bg-popover,
.dark .frosted-glass-dropdown.bg-popover,
.dark .frosted-glass-dialog.bg-popover,
.dark [data-radix-dropdown-menu-content].bg-popover {
  background: transparent !important;
  background-color: transparent !important;
}
</style>
